/* eslint-disable lfds/no-raw-token-strings -- --lfds-skeleton-* are internal custom properties from #utils/skeleton.css, not design tokens */
import tokens from '@lansforsakringar/core-tokens'
import { css } from 'lit'

import { vars, scoped, declare, override } from '#utils/styles'

/*
 * Field styles for the native `<select>` this component renders.
 *
 * Field metrics deliberately share `lfds-input`'s `--lfds--input-*` namespace,
 * so setting e.g. `--lfds--input-height` on an ancestor moves the select and
 * the text input together. Do not "fix" this to a recipe-specific prefix.
 */
const locals = scoped('input', [
	'background_color',
	'border_radius',
	'border_color',
	'padding',
	'height',
	'color'
] as const)

export default css`
	:host {
		${declare(locals, {
			background_color: tokens.color_background_secondary,
			border_radius: tokens.component_text_input_border_radius,
			border_color: tokens.color_border_focus,
			padding: tokens.spacing_sm,
			height: tokens.spacing_4xl,
			color: tokens.color_text_primary
		})};

		display: flex;
		width: 100%;
		flex-direction: column;
		color: ${vars(locals.color)};

		/* :not([loading]) -- the native select is also disabled while loading
		   (see lfds-select.ts), but that's only to block interaction, not to
		   look disabled -- the field keeps its normal chrome and only the
		   value/chevron shimmer, matching lfds-input. */
		&:not([loading]):has(.select:disabled) {
			${override(locals.color, tokens.color_text_disabled)};
		}
	}

	.header {
		display: flex;
		justify-content: space-between;
		align-items: flex-end;
		gap: ${vars(tokens.spacing_sm)};
		padding-block-end: ${vars(tokens.spacing_xs)};

		.header-text {
			display: flex;
			flex-direction: column;
		}

		/* The label element is always rendered, so that slot assignment stays
		   observable — collapse the chrome instead of removing it. */
		&.empty {
			display: none;
		}
	}

	.label {
		font-size: ${vars(tokens.text_size_label_default)};
		font-weight: ${vars(tokens.font_weight_semibold)};
		line-height: ${vars(tokens.line_height_label_default)};
		text-wrap: balance;
	}

	.box {
		display: flex;
		align-items: center;
		position: relative;
		overflow: hidden;
		background: ${vars(locals.background_color)};
		border-radius: ${vars(locals.border_radius)};
		border: 1px solid;
		color: ${vars(locals.color)};
		border-color: ${vars(locals.border_color)};
		transition: 125ms var(--ease-in);
		transition-property: box-shadow, border-color;

		/* Gray border when empty. A text input matches :placeholder-shown for this;
		   a <select> never can, so the class is set from script. */
		&.empty {
			${override(locals.border_color, tokens.color_border_tertiary)};
		}

		&:has(.select:where(:hover, :focus)) {
			${override(locals.border_color, tokens.color_border_focus)};

			box-shadow: inset 0 0 0 1px ${vars(locals.border_color)};
			transition-timing-function: var(--ease-out);
			outline: 0;
		}

		&.focus-visible:has(.select:focus-visible) {
			outline: var(--focus-outline);
			outline-offset: var(--focus-outline-offset);
		}

		&.invalid {
			${override(locals.border_color, tokens.color_border_error)};
		}

		&:not(.loading):has(.select:disabled) {
			${override(locals.border_color, tokens.color_border_disabled)};
			${override(locals.background_color, tokens.color_background_disabled)};

			box-shadow: none;
			transition-duration: 0ms;
		}

		/* Loading — field/border stay visible, only the value and chevron shimmer,
		   matching lfds-input's treatment (see .box.loading .select and
		   .box.loading .chevron below). */
		&.loading {
			pointer-events: none;
		}
	}

	.select {
		flex: 1;
		width: 100%;
		border: none;
		font-size: 1rem;
		line-height: calc(21 / 16);
		color: inherit;
		appearance: none;
		background: transparent;

		height: ${vars(locals.height)};
		/* Room for the absolutely positioned chevron (1.5rem), plus a gap. */
		padding: 0
			calc(${vars(locals.padding)} + ${vars(tokens.spacing_sm)} + 1.5rem) 0
			${vars(locals.padding)};
		white-space: nowrap;

		&:focus {
			outline: none;
		}
	}

	/*
	 * Loading — shimmer only a strip matching the select's own line-height
	 * (21/16 * 1rem), not the full field height. clip-path crops both the
	 * vertical strip and the horizontal value width (set inline per
	 * instance, since a native <select> can't host a sized ::after bar like
	 * other components) in one shape — its inset values match the content
	 * box exactly (same vertical centering, same start padding) so its
	 * round argument lands on the visible strip's actual corners. Rounding
	 * via background-clip: content-box alone doesn't work here: its corner
	 * radius is max(0, radius - padding), and padding on every side here
	 * is larger than the radius, clamping it to a hard 0.
	 */
	.box.loading .select {
		color: transparent;
		background: var(--lfds-skeleton-background);
		clip-path: inset(
			calc((${vars(locals.height)} - (21 / 16 * 1rem)) / 2)
				calc(100% - var(--lfds-skeleton-width, 60%))
				calc((${vars(locals.height)} - (21 / 16 * 1rem)) / 2)
				${vars(locals.padding)} round var(--lfds-skeleton-border-radius)
		);
		animation: var(--lfds-skeleton-animation);
	}

	.chevron {
		position: absolute;
		right: ${vars(locals.padding)};
		pointer-events: none;
	}

	/* Loading — mask the chevron icon in place with its own icon-sized shimmer */
	.box.loading .chevron {
		visibility: hidden;
	}

	.box.loading .chevron::after {
		content: '';
		visibility: visible;
		position: absolute;
		inset: 0;
		border-radius: var(--lfds-skeleton-border-radius);
		border: var(--lfds-skeleton-border);
		background: var(--lfds-skeleton-background);
		background-clip: padding-box;
		animation: var(--lfds-skeleton-animation);
	}

	/*
	 * Options and optgroups are only projected so this component can mirror them
	 * into the native <select>. Hide the projection point, otherwise slotted
	 * content (e.g. a divider <hr>) renders both here and inside the select.
	 */
	slot:not([name]) {
		display: none;
	}

	.optgroup {
		text-indent: ${vars(locals.padding)};
		font-size: 0.85rem;
		line-height: 2.6;
		text-transform: uppercase;
		letter-spacing: 0.5px;
	}

	.option {
		padding: ${vars(locals.padding)};
		font-size: 1rem;
		line-height: calc(21 / 16);
		color: ${vars(locals.color)};
		text-transform: none;
		text-indent: 0;
		background-color: ${vars(locals.background_color)};

		&:disabled {
			color: ${vars(tokens.color_text_disabled)};
		}
	}

	.divider {
		width: 100%;
		height: 0;
		margin: 0;
		border: none;
		border-bottom: 1px solid ${vars(locals.border_color)};
	}

	.help {
		cursor: default;
		font-size: ${vars(tokens.text_size_caption_default)};
		line-height: ${vars(tokens.line_height_caption_default)};
		color: ${vars(tokens.color_text_secondary)};
	}

	.validation {
		display: flex;
		align-items: flex-start;
		padding-block-start: ${vars(tokens.spacing_xs)};
		cursor: default;
		gap: ${vars(tokens.spacing_xs)};
		color: ${vars(tokens.color_text_error)};
		line-height: calc(18 / 14);
		font-size: 0.875rem;

		.icon {
			margin: -0.2rem 0 0 -0.15rem;
		}
	}

	/* Respect a user's stated preference for reduced motion (WCAG 2.3.3) */
	@media (prefers-reduced-motion: reduce) {
		.box {
			transition-duration: 0.01ms !important;
		}
	}
`
